import useUserVarStore from "@app/store/useUserVarStore";
import { GalleryCarouselTypes } from "@app/types/Opensearch.d";
import ArticleFollowTopic from "@components/Blocks/ArticleFollowTopic/ArticleFollowTopic";
import ArticleStickyBar from "@components/Blocks/ArticleStickyBar/ArticleStickyBar";
import AuthorCard from "@components/Blocks/Card/Author/AuthorCard";
import ReactionToolbar from "@components/Blocks/ReactionToolbar/ReactionToolbar";
import { SocialVariant } from "@components/Blocks/ShareSocial/ShareSocial";
import ShareSocialNew from "@components/Blocks/ShareSocial/ShareSocialNew";
import ArticleCarousel from "@components/Layouts/Carousel/ArticleCarousel";
import ArticleImage from "@components/Layouts/Carousel/ArticleImage";
import ArticleParagraph from "@elements/ArticleParagraph/ArticleParagraph";
import BeyondWordsPlayer from "@elements/BeyondWordsPlayer/BeyondWordsPlayer";
import GooglePreferredSourceButton from "@elements/GoogleSourceButton/GooglePreferredSourceButton";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import FollowTopicsToastMessage from "@elements/ToastMessage/FollowTopicsToastMessage";
import Date from "@elements/Typography/Date";
import Link from "@elements/Typography/Link";
import { StandFirst } from "@elements/Typography/Paragraph";
import { SectionTitleV2 } from "@elements/Typography/TitleV2";
import {
  BC_PLAYER_ID,
  BH_BC_ACCOUNT_ID,
  ENABLE_ARTICLE_REDESIGN,
} from "@helper/getEnvVariables";
import getFallbackSectionImage from "@helper/getFallbackSectionImage";
import { shouldShowTTSPlayer } from "@helper/showTTS";
import toCarouselItem from "@helper/toCarouselItem";
import {
  findStyleBySectionAndSubCategory,
  isArrayWithElements,
} from "@helper/utils";
import { useArticleReactions } from "@hooks/useArticleReactions";
import useFollowTags from "@hooks/useFollowTags";
import useMySPHLightbox from "@hooks/useMySPHLightbox";
import {
  ArticleMediaCarousel,
  type CarouselImageItem,
  type CarouselItem,
  CarouselItemType,
} from "@sphtech/sph-web-ui";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import { NoSSR } from "@sphtech/web2-core/components";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";

import ArticleElementWrapper from "../Components/ArticleElementWrapper";
import ContentAccessLabel from "../Components/ContentAccessLabel";

export type ArticleContentCardNewProps = {
  mainArticle: ProcessedArticleData;
  pageNumber: number;
  isInfiniteScrollArticle?: boolean;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  index?: number;
  visibleArticleId?: string;
  trendingArticles?: ProcessedArticleData[];
};

export default function ArticleContentCardNew({
  mainArticle,
  pageNumber,
  isInfiniteScrollArticle = false,
  gsChannels,
  articleSlotTarget,
  index,
  visibleArticleId,
  trendingArticles,
}: ArticleContentCardNewProps) {
  const { userToken } = useUserVarStore();
  const {
    sectionName,
    uniqueSectionName,
    title,
    tags,
    authors,
    date,
    datetime,
    time,
    articleBody,
    standFirst,
    contentAccess,
    urlPath,
    imageGallery,
    relatedArticles,
    attachments,
    id,
    sourceId,
    linkToSection,
    adsUniqueSectionName,
    campaignReference,
  } = mainArticle;

  const {
    reactions,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: id,
    userToken,
    publishDate: datetime,
    uniqueSectionName,
  });

  const { lightbox } = useMySPHLightbox();
  const { visitorId, isFollowing, toggleFollow, loadingTopicId, showToast } =
    useFollowTags({
      onRequireLogin: () => lightbox?.initiateLogin(),
    });

  const socialIconList = ["facebook", "telegram", "plainWhatsapp", "newsPost"];
  const pageUrl = `${urlPath}`;
  const renderItems = imageGallery.filter(
    (item) => item.type !== GalleryCarouselTypes.UNKNOWN,
  );
  const sectionColor = mainArticle.parentSection
    ? findStyleBySectionAndSubCategory(mainArticle.parentSection)
        ?.selectedColor || ""
    : findStyleBySectionAndSubCategory(sectionName || "")?.selectedColor || "";
  const showTTSPlayer = shouldShowTTSPlayer(datetime, contentAccess);
  const isCampaign = Array.isArray(campaignReference)
    ? campaignReference.length > 0
    : !!(campaignReference?.href && campaignReference.title);
  const elementLink = isCampaign
    ? `mata-mata/${campaignReference?.href.split("/").at(-1)}`
    : linkToSection;

  const sectionTitleProps = {
    Element: Link,
    className:
      "text-[16px] lg:text-[12px] lg:font-bold mb-0 leading-[120%]" +
      (isCampaign ? " text-blue-300 hover:text-blue-700 hover:underline" : ""),
    sectionName: isCampaign ? campaignReference?.title : sectionName,
    parentSection: isCampaign ? sectionName : mainArticle.parentSection,
    iconStyle: "hidden" as const,
  };

  const [isStickyVisible, setIsStickyVisible] = useState(false);

  const firstTag = tags?.[0];

  const ttsParentRef = useRef<HTMLDivElement>(null);
  const isArticleRedesignEnabled = ENABLE_ARTICLE_REDESIGN;

  // Update CSS variable for TTS player parent width.
  useEffect(() => {
    if (!isArticleRedesignEnabled) return;

    const parent = ttsParentRef.current;

    if (!parent) return;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      parent.style.setProperty("--standard-width", `${width}px`);
    });

    observer.observe(parent);

    return () => {
      observer.disconnect();
      parent.style.removeProperty("--standard-width");
    };
  }, [isArticleRedesignEnabled]);

  const carouselItems = useMemo(() => {
    if (!isArrayWithElements(imageGallery)) {
      const fallbackItem: CarouselImageItem = {
        id: "fallback",
        type: CarouselItemType.IMAGE,
        image: {
          url: getFallbackSectionImage(sectionName),
        },
        altText: "Fallback image",
        caption: "",
        credit: "",
      };
      return [fallbackItem];
    }

    return imageGallery
      .map((item, idx) => toCarouselItem(item, sectionName, idx))
      .filter((item): item is CarouselItem => item !== null);
  }, [imageGallery, sectionName]);

  const renderAuthors = useMemo(() => {
    const authorImageWrapperStyle = isArticleRedesignEnabled
      ? "w-[48px] p-[3px]"
      : "w-[46px] rounded-[55px] border border-border-strong";

    const authorImageStyle = isArticleRedesignEnabled
      ? "border border-[#CFCFCF]"
      : undefined;

    return (
      <>
        {isArrayWithElements(authors) && authors.some((a) => a.hasProfile) && (
          <div className="flex flex-row gap-3 flex-wrap">
            {authors
              .filter((author) => author.hasProfile)
              .map((author, index) => (
                <AuthorCard
                  key={index}
                  author={author}
                  displayHeight={isArticleRedesignEnabled ? 42 : 44}
                  displayWidth={isArticleRedesignEnabled ? 42 : 44}
                  imageWrapper={authorImageWrapperStyle}
                  authorCardWrapper="gap-[5px]"
                  imageStyle={authorImageStyle}
                  nameClass={`text-[12px] lg:text-[12px] font-semibold text-brand-primary leading-[150%]`}
                  hasEmail={false}
                  forAuthorListing={false}
                />
              ))}
          </div>
        )}
      </>
    );
  }, [authors, isArticleRedesignEnabled]);

  const renderMetaSection = () => (
    <div className="flex flex-col gap-2">
      <div
        className={`flex w-full gap-3 ${isArticleRedesignEnabled ? "lg:pt-1 pb-2.5 lg:pb-3 border-b border-[#C0C0C4]" : "mb-6 lg:mb-8 pt-[3px]"}`}
      >
        <div
          className={`flex flex-col-reverse flex-grow-no-shrink justify-center ${isArticleRedesignEnabled ? "gap-2" : "gap-1"}`}
        >
          <div className="flex">
            <Date
              date={date}
              time={time}
              variant="or"
              className={`font-medium leading-[150%] text-text-muted ${isArticleRedesignEnabled ? "text-[16px] md:text-[16px]" : "text-[14px]"}`}
              dateTime={datetime}
            />
          </div>
          {renderAuthors}
        </div>

        {isArticleRedesignEnabled && (
          <GooglePreferredSourceButton
            className="w-auto min-w-[125px]"
            pageUrl={urlPath || ""}
          />
        )}

        {!isArticleRedesignEnabled && (
          <ReactionToolbar
            articleId={id}
            showReactions={showReactions}
            reactions={reactions}
            userReactions={userReactions}
            reactionsError={reactionsError}
            handleReactionClick={handleReactionClick}
          />
        )}
      </div>

      {isArticleRedesignEnabled && (
        <ArticleStickyBar
          article={mainArticle}
          pageNumber={pageNumber}
          isStatic={true}
          gsChannels={gsChannels}
          articleSlotTarget={articleSlotTarget}
        />
      )}
    </div>
  );

  return (
    <div
      key={index}
      id={`${id}-article-body`}
      data-testid={`${id}-article-body`}
      className={classNames({ v_showStickyBar: isStickyVisible })}
    >
      <FollowTopicsToastMessage forceShowToast={showToast} />
      <ArticleStickyBar
        article={mainArticle}
        pageNumber={pageNumber}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
        onVisibilityChange={setIsStickyVisible}
        isActive={visibleArticleId === id}
      />
      <ArticleElementWrapper>
        <div
          className={`flex items-center pb-3 mb-2 border-b-2 border-${sectionColor}`}
          data-testid="section-share-wrapper"
        >
          <SectionTitleV2 {...sectionTitleProps} elementLink={elementLink} />
        </div>
        <div className="flex flex-col gap-2 mb-2">
          <ContentAccessLabel
            contentAccess={contentAccess}
            showIcon={contentAccess === LabelCardIconVariety.PREMIUM}
          />
          <Heading
            Element="h1"
            className="font-secondary leading-[120%] text-text-body text-[22px] lg:text-[38px] font-semibold article-headline"
            sourceId={sourceId}
          >
            {title}
          </Heading>
          {standFirst && (
            <StandFirst className="text-xs lg:text-xs font-primary font-medium leading-[150%] text-neutral-700">
              {standFirst}
            </StandFirst>
          )}
        </div>
        {!isArticleRedesignEnabled && (
          <ArticleStickyBar
            article={mainArticle}
            pageNumber={pageNumber}
            isStatic={true}
            gsChannels={gsChannels}
            articleSlotTarget={articleSlotTarget}
          />
        )}

        {isArticleRedesignEnabled ? (
          <div className="flex gap-2 mb-4 min-h-[48px]" ref={ttsParentRef}>
            <div className="flex-grow-no-shrink">
              {showTTSPlayer && (
                <BeyondWordsPlayer
                  cueId={id}
                  title={title}
                  articleId={id}
                  isInfiniteScrollArticle={isInfiniteScrollArticle}
                  visibleArticleId={visibleArticleId}
                />
              )}
            </div>
            <ReactionToolbar
              articleId={id}
              showReactions={showReactions}
              reactions={reactions}
              userReactions={userReactions}
              reactionsError={reactionsError}
              handleReactionClick={handleReactionClick}
            />
          </div>
        ) : (
          showTTSPlayer && (
            <BeyondWordsPlayer
              cueId={id}
              title={title}
              articleId={id}
              isInfiniteScrollArticle={isInfiniteScrollArticle}
              visibleArticleId={visibleArticleId}
            />
          )
        )}
        {isArticleRedesignEnabled ? (
          carouselItems.length === 1 &&
          carouselItems[0].id.startsWith("fallback") &&
          carouselItems[0].type === CarouselItemType.IMAGE ? (
            <ArticleImage
              url={carouselItems[0].image?.url}
              alt={carouselItems[0].altText || carouselItems[0].id}
              sectionName={sectionName}
              caption={carouselItems[0].caption}
              credit={carouselItems[0].credit}
              className="max-w-[860px] mb-6 lg:mb-8"
            />
          ) : (
            <ArticleMediaCarousel
              items={carouselItems}
              brightcovePlayerId={BC_PLAYER_ID}
              brightcoveAccountId={BH_BC_ACCOUNT_ID}
              className="max-w-[860px] mb-4"
            />
          )
        ) : (
          isArrayWithElements(renderItems) && (
            <ArticleCarousel
              renderItems={renderItems}
              sectionName={sectionName}
              wrapperClass="max-w-[860px]"
            />
          )
        )}
        <div
          className={`flex flex-col gap-2 lg:gap-4 ${isArticleRedesignEnabled ? "mb-6 lg:mb-8" : ""}`}
        >
          {firstTag && !isArticleRedesignEnabled && (
            <ArticleFollowTopic
              label={firstTag.name ?? ""}
              topicId={firstTag.alias ?? ""}
              onFollow={toggleFollow}
              onUnfollow={toggleFollow}
              isFollowing={isFollowing(firstTag.alias ?? "")}
              isLoading={loadingTopicId === firstTag.alias}
              loggedIn={visitorId !== ""}
            />
          )}

          {renderMetaSection()}
        </div>
      </ArticleElementWrapper>

      <ArticleParagraph
        freeArticle={mainArticle}
        articleId={id}
        contentAccess={contentAccess}
        articleBody={articleBody}
        uniqueSectionName={uniqueSectionName}
        relatedArticles={relatedArticles}
        pageNumber={pageNumber}
        isInfiniteScrollArticle={isInfiniteScrollArticle}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
        visibleArticleId={visibleArticleId}
        adsUniqueSectionName={adsUniqueSectionName}
        attachments={attachments}
        tags={tags}
        showReactions
        reactions={reactions}
        handleReactionClick={handleReactionClick}
        userReactions={userReactions}
        trendingArticles={trendingArticles}

        toggleFollow={toggleFollow}
        isFollowing={isFollowing}
        loadingTopicId={loadingTopicId}
        visitorId={visitorId}
      />

      <ArticleElementWrapper>
        <div
          className="flex self-end items-center justify-end my-6 lg:my-8 track-impression"
          data-section-name="end-of-article"
        >
          <NoSSR>
            {socialIconList.map((variant, index) => (
              <ShareSocialNew
                variant={variant as SocialVariant}
                pagePath={pageUrl}
                pageTitle={title}
                dateCreated={datetime}
                key={index}
              />
            ))}
          </NoSSR>
        </div>
      </ArticleElementWrapper>
    </div>
  );
}
